import { notificationApi } from './api';
import { isWebPushApiSupported, shouldSuggestChromeOnAndroidForPush } from './deviceDetection';
import { isRunningAsPwa } from './pwaRuntime';

const WEB_PUSH_ENABLED_STORAGE_KEY = 'fdh-web-push-enabled';

const urlBase64ToArrayBuffer = (base64String: string): ArrayBuffer => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const output = Uint8Array.from([...rawData].map((ch) => ch.charCodeAt(0)));
  return output.buffer;
};

const isPushSupported = (): boolean => isWebPushApiSupported();

const readDevicePushPreference = (): boolean => {
  try {
    const raw = localStorage.getItem(WEB_PUSH_ENABLED_STORAGE_KEY);
    if (raw === null) {
      return true;
    }
    return raw === 'true';
  } catch {
    return true;
  }
};

const writeDevicePushPreference = (enabled: boolean): void => {
  try {
    localStorage.setItem(WEB_PUSH_ENABLED_STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    // ignore localStorage write failures
  }
};

export const shouldShowWebPushSetting = (): boolean => {
  if (shouldSuggestChromeOnAndroidForPush()) {
    return false;
  }
  return isPushSupported() && isRunningAsPwa();
};

export const isWebPushEnabledOnDevice = (): boolean => readDevicePushPreference();

export const getBrowserNotificationPermission = (): NotificationPermission =>
  isPushSupported() ? Notification.permission : 'default';

const trySubscribePush = async (allowPermissionPrompt: boolean): Promise<void> => {
  if (!shouldShowWebPushSetting()) {
    return;
  }

  if (!readDevicePushPreference()) {
    return;
  }

  const keyResponse = await notificationApi.getPushPublicKey();
  if (!keyResponse.success || !keyResponse.data?.enabled || keyResponse.data.publicKey.trim() === '') {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const currentSubscription = await registration.pushManager.getSubscription();
  if (currentSubscription) {
    await notificationApi.subscribePush(currentSubscription.toJSON());
    return;
  }

  if (Notification.permission === 'denied') {
    return;
  }

  if (!allowPermissionPrompt && Notification.permission !== 'granted') {
    return;
  }

  if (allowPermissionPrompt && Notification.permission === 'default') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return;
    }
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToArrayBuffer(keyResponse.data.publicKey),
  });

  await notificationApi.subscribePush(subscription.toJSON());
};

export const enableWebPushIfPossible = async (): Promise<void> => {
  try {
    await trySubscribePush(false);
  } catch {
    // Keep push setup best-effort, never block app flow.
  }
};

export const requestWebPushPermissionAndSubscribe = async (): Promise<void> => {
  await trySubscribePush(true);
};

export const disableWebPushForCurrentDevice = async (): Promise<void> => {
  if (!isPushSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      return;
    }

    await notificationApi.unsubscribePush(subscription.endpoint);
    await subscription.unsubscribe();
  } catch {
    // Logout should continue even if push cleanup fails.
  }
};

export const setWebPushEnabledOnDevice = async (enabled: boolean): Promise<void> => {
  writeDevicePushPreference(enabled);

  if (!shouldShowWebPushSetting()) {
    return;
  }

  if (enabled) {
    await requestWebPushPermissionAndSubscribe();
    return;
  }

  await disableWebPushForCurrentDevice();
};
